import type { CSSProperties } from "react";
import { MapBorderType } from "../enum/map";
import { ApiMap, ApiMapBorder, ApiMapCard, ApiMapCardAssetType, ApiMapContinent, ApiMapCountry } from "../types/apimap";
import { MAP_CUSTOM_BACKGROUND_COLORS } from "../types/color";
import getItemById from "../util/getItemById";
import { ENVIRONMENT_IS_DEVELOPMENT } from "../util/development";
import { UserFacingError } from "../server/types/error";
import { urlImageCard } from "../consts/urls";

export const GAME_MAP_NAME_MAX_LENGTH = 32;
const GAME_MAP_NAME_REGEX = /^[a-zA-Z0-9_-]+$/;

export default class GameMap {
    constructor(public map: ApiMap) { }

    static validateMapShortName(mapName: string): void {
        if (mapName.length <= 0) {
            throw new UserFacingError('Map short name cannot be empty');
        }

        if (mapName.length > GAME_MAP_NAME_MAX_LENGTH) {
            throw new UserFacingError(`Map short name can only be ${GAME_MAP_NAME_MAX_LENGTH} characters`);
        }

        if (!GAME_MAP_NAME_REGEX.exec(mapName)) {
            throw new UserFacingError('Map short name can only have alphanumeric characters, underscore and hyphen');
        }
    }

    getAllContinentsByCountryId(countryId: number): ApiMapContinent[] {
        return this.map.data.continents.filter(
            (continent) => continent.countries.includes(countryId),
        );
    }

    getBackgroundStyle(mapCustomBackgroundColorIndex?: number): CSSProperties {
        const style: CSSProperties = {};
        const backgroundImageUrl = this.getBackgroundImageUrl();

        if (backgroundImageUrl) {
            style.backgroundImage = `url(${backgroundImageUrl})`;
        } else {
            style.backgroundColor = this.getCustomBackgroundColor(
                mapCustomBackgroundColorIndex,
            );
        }

        return style;
    }

    getBackgroundImageUrl(): string {
        const baseUrl = this.map?.data?.backgroundImage;
        if (!baseUrl) {
            return null;
        }

        const sanitizedBaseUrl = encodeURI(baseUrl);

        if (ENVIRONMENT_IS_DEVELOPMENT) {
            return sanitizedBaseUrl;
        } else {
            return `https://static.spqr.app/public${sanitizedBaseUrl}`;
        }
    }

    getCardAssetUrl(card: ApiMapCard): string {
        const cardPrefix = this.getCardPrefix(card);

        if (ENVIRONMENT_IS_DEVELOPMENT) {
            return `${cardPrefix}/${card.id}.jpg`;
        } else {
            return urlImageCard(cardPrefix, card.id);
        }
    }

    getCardPrefix(card: ApiMapCard): string {
        if (card.assetType === ApiMapCardAssetType.Custom) {
            return `/maps/${this.map.shortName}`;
        } else if (this.map.cardPrefix) {
            // legacy support
            return this.map.cardPrefix;
        } else {
            return '/cards';
        }
    }

    getCardAssetPath(card: ApiMapCard): string {
        const cardPrefix = this.getCardPrefix(card);
        return `${cardPrefix}/${card.id}.jpg`
    }

    getCardById(cardId: number) {
        return this.map.data.cards.find(
            (card) => card.id === cardId,
        );
    }

    getCountryByDomId(domId: string): ApiMapCountry {
        return getItemById(this.map.data.countries, domId, 'domId');
    }

    getCountryById(id: number): ApiMapCountry {
        return getItemById(this.map.data.countries, id);
    }

    getContinentById(id: number): ApiMapContinent {
        return getItemById(this.map.data.continents, id);
    }

    getCustomBackgroundColor(
        mapCustomBackgroundColorIndex?: number,
    ): string | undefined {
        if (mapCustomBackgroundColorIndex > 0) {
            const hex = MAP_CUSTOM_BACKGROUND_COLORS.get(mapCustomBackgroundColorIndex)?.hex;
            if (hex) {
                return hex;
            }
        }

        return this.map?.data?.backgroundColor ?? undefined;
    }

    getNeighbors(countryId: number): ApiMapCountry[] {
        return this.map.data.countries
            .filter(
                (country: ApiMapCountry) => this.hasBorder(countryId, country.id),
            );
    }

    getContinentNeighbors(continentId: number): number[] {
        const neighborContinentsSet = new Set<number>();
        const continent = this.getContinentById(continentId);

        for (const countryId of continent.countries) {
            const neighborCountries = this.getNeighbors(countryId);

            for (const neighborCountry of neighborCountries) {
                const neighborContinents = this.getAllContinentsByCountryId(neighborCountry.id);

                for (const neighborContinent of neighborContinents) {
                    if (neighborContinent.id !== continentId) {
                        neighborContinentsSet.add(neighborContinent.id);
                    }
                }
            }
        }

        return [...neighborContinentsSet].sort((a, b) => a - b);
    }

    getBorder(fromCountryId: number, toCountryId: number): ApiMapBorder {
        return this.map.data.borders
            .find(
                (border: ApiMapBorder) => border.from === fromCountryId && border.to === toCountryId
                    || (border.type !== MapBorderType.ONE_WAY &&
                        border.to === fromCountryId && border.from === toCountryId)
            );
    }

    hasBorder(fromCountryId: number, toCountryId: number): boolean {
        return this.getBorder(fromCountryId, toCountryId) != null;
    }
}
